import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import './ChatPanel.css';
import { IoArrowUp, IoChevronDown, IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { BsEmojiSmile, BsMicFill, BsMicMuteFill } from 'react-icons/bs'; // mic icons used by People rows
import EmojiPicker, { EmojiStyle } from 'emoji-picker-react';
import { createUserAvatarDataUri } from '../utils/avatarUtils';

// The picker's default (Apple) style draws every emoji as an image from
// cdn.jsdelivr.net. The dedicated (on-prem) edition contacts only the platform
// (P-34), so it draws the device's own emoji glyphs instead; the SaaS keeps the
// default look.
const EMOJI_STYLE = process.env.REACT_APP_EDITION === 'onprem' ? EmojiStyle.NATIVE : undefined;

const GROUP_GAP_MS = 5 * 60 * 1000; // 5 min — bubbles from the same sender within this window are grouped
const TIME_DIVIDER_GAP_MS = 15 * 60 * 1000; // 15 min — show a date/time divider line

// Matches absolute URLs (http/https) and bare www. URLs. Captures up to the first
// whitespace or quote; trailing punctuation is trimmed off below so "see https://x.com."
// renders as a link without swallowing the period.
const URL_REGEX = /\b(https?:\/\/[^\s<>"']+|www\.[a-z0-9.-]+\.[a-z]{2,}[^\s<>"']*)/gi;
const TRAILING_PUNCT = /[.,!?;:)\]]+$/;

function linkifyText(text) {
  if (!text || typeof text !== 'string') return text;
  const out = [];
  let last = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    let raw = m[0];
    // Peel off trailing punctuation so it stays as plain text after the link.
    const trail = (raw.match(TRAILING_PUNCT) || [''])[0];
    if (trail) raw = raw.slice(0, raw.length - trail.length);
    const href = raw.startsWith('www.') ? `https://${raw}` : raw;
    out.push(
      <a
        key={`lnk-${m.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="chat-link"
        onClick={(e) => e.stopPropagation()}
      >
        {raw}
      </a>
    );
    if (trail) out.push(trail);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length > 0 ? out : text;
}

function formatBubbleTime(date) {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDividerTime(date) {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const t = formatBubbleTime(date);
  if (isToday)     return `Today ${t}`;
  if (isYesterday) return `Yesterday ${t}`;
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ' ' + t;
}

function groupMessages(messages, selfKey) {
  // Sort by time ascending so groups read top-down chronologically (iMessage convention).
  const sorted = [...messages].sort((a, b) => new Date(a.time) - new Date(b.time));
  const out = [];
  let prevTime = null;

  for (const msg of sorted) {
    const time = new Date(msg.time);
    const isSelf = msg.from === selfKey;
    const last = out[out.length - 1];

    // Insert a time divider if there's a big gap since the last message
    if (prevTime && time - prevTime > TIME_DIVIDER_GAP_MS) {
      out.push({ kind: 'divider', label: formatDividerTime(time), key: `d-${time.getTime()}` });
    }

    const lastGroup = last?.kind === 'group' ? last : null;
    const sameSender = lastGroup && lastGroup.from === msg.from;
    const closeInTime = lastGroup && (time - new Date(lastGroup.messages[lastGroup.messages.length - 1].time)) < GROUP_GAP_MS;

    if (sameSender && closeInTime) {
      lastGroup.messages.push(msg);
    } else {
      out.push({
        kind: 'group',
        from: msg.from,
        isSelf,
        avatar: msg.avatar,
        messages: [msg],
      });
    }
    prevTime = time;
  }
  return out;
}

/**
 * ChatPanel — unified Chat + People panel.
 *
 * Tabs at the top switch between "Chat" (messages + composer) and "People"
 * (per-participant mute + mute-all). Only the views that are enabled show:
 *   - textChat only  → Chat view, no tabs
 *   - voiceChat only → People view, no tabs
 *   - both           → tab switcher
 *
 * Responsive: full-screen slide-up on mobile, side slide-in on desktop.
 *
 * Props (chat):
 *   messages, selfKey, onSendMessage, onTyping, typingUsers, newMessages,
 *   showChat (bool — whether the Chat view is available)
 *
 * Props (people / audio group):
 *   showPeople (bool — whether the People view is available),
 *   participants            [{ id, avatar, speaking, ... }],
 *   mutedParticipants       { [id]: boolean },
 *   onToggleParticipant     fn(id, nextMuted),
 *   muteAll, onToggleMuteAll,
 *
 * Props (chrome):
 *   isOpen, onClose, title, isMobile, darkMode, position,
 *   showMic, micOn, onToggleMic
 */
export default function ChatPanel({
  messages = [],
  selfKey = 'You',
  onSendMessage,
  onTyping,
  typingUsers = [],
  isOpen = true,
  onClose,
  title = 'Chat',
  isMobile = false,
  darkMode = true,
  newMessages = 0,
  position = 'Right',
  showChat = true,
  showPeople = false,
  participants = [],
  mutedParticipants = {},
  onToggleParticipant,
  muteAll = false,
  onToggleMuteAll,
}) {
  const [input, setInput] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  // Tab: 'chat' or 'people'. Default to the first available view.
  const [activeTab, setActiveTab] = useState(showChat ? 'chat' : 'people');

  // Re-snap to a valid tab if the available views change.
  useEffect(() => {
    if (activeTab === 'chat'   && !showChat)   setActiveTab(showPeople ? 'people' : 'chat');
    if (activeTab === 'people' && !showPeople) setActiveTab(showChat   ? 'chat'   : 'people');
  }, [showChat, showPeople, activeTab]);

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef(null);
  const bottomAnchorRef = useRef(null);
  const lastMessageCount = useRef(messages.length);

  // Mobile: pin the panel to the visual-viewport height and lock the body so iOS
  // can't scroll the page when the keyboard pushes the focused input into view.
  // useLayoutEffect (not useEffect) so the height/top/body-lock are applied
  // before the browser paints — otherwise there's a visible frame where the
  // panel shows with raw CSS defaults before the JS snaps it into place.
  useLayoutEffect(() => {
    if (!isMobile || !isOpen) return;
    const vv = window.visualViewport;
    const panel = panelRef.current;
    if (!panel) return;

    // Lock the page so iOS can't scroll the focused input into view (which on iPhone
    // is what makes the panel "drift up for no reason" when tapping the input).
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      scrollY: window.scrollY,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${prev.scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Continuous per-frame sync. iOS fires visualViewport resize/scroll events
    // only sparsely during the keyboard slide animation, so an event-driven
    // handler updates in big steps — the panel visibly "jumps up then settles".
    // A requestAnimationFrame loop reads the visual viewport every frame and
    // pins the panel to it, so it tracks the keyboard smoothly with no lag.
    // The h/t guard skips redundant style writes when nothing changed.
    let rafId = null;
    if (vv) {
      let lastH = -1, lastT = -1;
      const tick = () => {
        const h = vv.height;
        const t = vv.offsetTop;
        if (h !== lastH || t !== lastT) {
          panel.style.height = `${h}px`;
          panel.style.top = `${t}px`;
          lastH = h; lastT = t;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      panel.style.height = '';
      panel.style.top = '';
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      document.body.style.overflow = prev.overflow;
      // Restore the scroll position we were at before locking.
      window.scrollTo(0, prev.scrollY);
    };
  }, [isMobile, isOpen]);

  // While the chat textarea is focused, swallow keydown events at the window
  // (capture phase) so PixelStreaming doesn't forward W/A/S/D/space/etc. to UE.
  // Enter still propagates so any global "send" handlers keep working.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (document.activeElement !== inputRef.current) return;
      if (e.key === 'Enter' && !e.shiftKey) return; // handled locally by the textarea
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
    };
  }, [isOpen]);

  const items = useMemo(() => groupMessages(messages, selfKey), [messages, selfKey]);

  // Auto-scroll to bottom when new messages arrive AND we were already at the bottom
  useEffect(() => {
    if (messages.length > lastMessageCount.current) {
      if (atBottom) {
        bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
    lastMessageCount.current = messages.length;
  }, [messages, atBottom]);

  // Initial scroll-to-bottom on open
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        bottomAnchorRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      });
    }
  }, [isOpen]);

  // Track whether the user has scrolled away from the bottom
  const onScroll = (e) => {
    const el = e.currentTarget;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAtBottom(dist < 80);
  };

  // Auto-grow textarea up to a max height; broadcast a throttled typing ping.
  const onInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    if (value.trim().length > 0) onTyping?.();
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    onSendMessage?.(text);
    setInput('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.focus();
    }
    setShowEmoji(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const onEmojiClick = (emojiData) => {
    setInput(prev => prev + emojiData.emoji);
    inputRef.current?.focus();
  };

  const canSend = input.trim().length > 0;
  const empty = items.length === 0;
  const typingLabel = useMemo(() => {
    if (!typingUsers || typingUsers.length === 0) return null;
    const names = typingUsers.map(u => (u.id || '').split('&')[0]).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} is typing`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
    return `${names[0]}, ${names[1]} and ${names.length - 2} more are typing`;
  }, [typingUsers]);

  return (
    <div
      ref={panelRef}
      className={[
        'chat-panel',
        isMobile ? 'chat-panel-mobile' : 'chat-panel-desktop',
        darkMode ? 'chat-panel-dark' : 'chat-panel-light',
        isOpen ? 'chat-panel-open' : '',
        !isMobile && position === 'Left' ? 'chat-panel-anchor-left' : 'chat-panel-anchor-right',
      ].join(' ')}
      role="dialog"
      aria-label="Chat"
    >
      {/* Header */}
      <div className="chat-panel-header">
        {showChat && showPeople ? (
          <div className="chat-panel-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'chat'}
              className={`chat-panel-tab ${activeTab === 'chat' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              Chat
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'people'}
              className={`chat-panel-tab ${activeTab === 'people' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('people')}
            >
              People{participants.length > 0 ? ` · ${participants.length}` : ''}
            </button>
          </div>
        ) : (
          <div className="chat-panel-title">
            {showPeople && !showChat ? 'People' : title}
          </div>
        )}
        {onClose && (
          <button className="chat-panel-icon-btn" onClick={onClose} aria-label="Close panel">
            {isMobile
              ? <IoChevronDown size={22} />
              : (position === 'Left' ? <IoChevronBack size={20} /> : <IoChevronForward size={20} />)}
          </button>
        )}
      </div>

      {/* People view */}
      {activeTab === 'people' && showPeople && (
        <div className="chat-panel-people">
          {participants.length === 0 ? (
            <div className="chat-panel-people-empty">No other participants in the room.</div>
          ) : (
            <div className="chat-panel-people-list">
              {participants.map((p) => {
                const muted = !!mutedParticipants[p.id];
                const name = (p.id || '').split('&')[0];
                // Regenerated locally from identity — never render remote avatar HTML.
                const avatarUri = createUserAvatarDataUri(p.id || name);
                return (
                  <div className="chat-panel-people-row" key={p.id}>
                    <div className="chat-panel-people-avatar">
                      {avatarUri ? (
                        <img src={avatarUri} alt={name} />
                      ) : (
                        <div className="chat-panel-people-avatar-fallback">{(name[0] || '?').toUpperCase()}</div>
                      )}
                      {p.speaking && <span className="chat-panel-people-speaking" />}
                    </div>
                    <div className="chat-panel-people-name" title={name}>{name}</div>
                    <button
                      className={`chat-panel-people-mute ${muted ? 'is-muted' : ''}`}
                      onClick={() => onToggleParticipant?.(p.id, !muted)}
                      aria-label={muted ? `Unmute ${name}` : `Mute ${name}`}
                      title={muted ? `Unmute ${name}` : `Mute ${name}`}
                    >
                      {muted ? <BsMicMuteFill size={14} /> : <BsMicFill size={14} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="chat-panel-people-footer">
            <button
              className="chat-panel-people-all-btn"
              onClick={() => onToggleMuteAll?.(!muteAll)}
              disabled={participants.length === 0}
            >
              {muteAll ? 'Unmute all' : 'Mute all'}
            </button>
          </div>
        </div>
      )}

      {/* Chat view: messages */}
      {activeTab === 'chat' && showChat && (
      <div className="chat-panel-messages" ref={messagesRef} onScroll={onScroll}>
        {empty ? (
          <div className="chat-panel-empty">
            <div className="chat-panel-empty-icon">✦</div>
            <div className="chat-panel-empty-title">No messages yet</div>
            <div className="chat-panel-empty-sub">Send the first message to start the conversation.</div>
          </div>
        ) : (
          items.map((item, idx) => {
            if (item.kind === 'divider') {
              return <div className="chat-divider" key={item.key}><span>{item.label}</span></div>;
            }
            // group
            const group = item;
            const lastMsgTime = new Date(group.messages[group.messages.length - 1].time);
            return (
              <div key={`g-${idx}`} className={`chat-group ${group.isSelf ? 'chat-self' : 'chat-other'}`}>
                {!group.isSelf && (
                  <div className="chat-group-sender">{(group.from || '').split('&')[0]}</div>
                )}
                {group.messages.map((msg, mi) => {
                  const isLast = mi === group.messages.length - 1;
                  return (
                    <div
                      key={mi}
                      className={`chat-bubble ${isLast ? 'chat-bubble-last' : ''}`}
                    >
                      {linkifyText(msg.text)}
                    </div>
                  );
                })}
                <div className="chat-group-time">{formatBubbleTime(lastMsgTime)}</div>
              </div>
            );
          })
        )}
        {typingLabel && (
          <div className="chat-typing">
            <div className="chat-typing-bubble" aria-label={typingLabel}>
              <span className="chat-typing-dot" />
              <span className="chat-typing-dot" />
              <span className="chat-typing-dot" />
            </div>
            <div className="chat-typing-label">{typingLabel}</div>
          </div>
        )}
        <div ref={bottomAnchorRef} />
      </div>
      )}

      {/* Jump-to-bottom button when scrolled up — chat view only */}
      {activeTab === 'chat' && showChat && !atBottom && (
        <button
          className="chat-panel-jump"
          onClick={() => bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Jump to latest"
        >
          <IoChevronDown size={18} />
          {newMessages > 0 && <span className="chat-panel-jump-badge">{newMessages}</span>}
        </button>
      )}

      {/* Emoji picker — chat view only */}
      {activeTab === 'chat' && showChat && showEmoji && (
        <div className="chat-panel-emoji">
          <EmojiPicker
            onEmojiClick={onEmojiClick}
            theme={darkMode ? 'dark' : 'light'}
            width="100%"
            height={300}
            previewConfig={{ showPreview: false }}
            searchDisabled={isMobile}
            {...(EMOJI_STYLE ? { emojiStyle: EMOJI_STYLE } : {})}
          />
        </div>
      )}

      {/* Composer — chat view only */}
      {activeTab === 'chat' && showChat && (
      <div className="chat-panel-composer">
        <button
          className={`chat-panel-emoji-btn ${showEmoji ? 'active' : ''}`}
          onClick={() => setShowEmoji(v => !v)}
          aria-label="Emoji picker"
        >
          <BsEmojiSmile size={20} />
        </button>
        <div className="chat-panel-input-wrap">
          <textarea
            ref={inputRef}
            className="chat-panel-input"
            value={input}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Send a message"
            rows={1}
          />
        </div>
        <button
          className={`chat-panel-send ${canSend ? 'chat-panel-send-visible' : ''}`}
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send"
        >
          <IoArrowUp size={18} strokeWidth={2.4} />
        </button>
      </div>
      )}
    </div>
  );
}
